"use client";

/**
 * AlertDashboardView — Dashboard tab on /alerts/[id].
 *
 * Renders the alert's rendered HTML dashboard snapshot:
 *   - owner / paid LIVE  → /snapshots/latest (freshest, polls 30s)
 *   - free / delayed     → /snapshots/first  (stale teaser, payment-gated)
 *
 * Prefers the PKA-generated single image when present, else iframes the
 * raw HTML. Body also exposes Download HTML + Download data.
 *
 * Legacy automations keep working: this just shows whatever snapshot the
 * run captured — no new data shape required.
 */

import useSWR from "swr";
import { Loader2, BarChart3 } from "lucide-react";
import { apiClient, type AlertSnapshot } from "@/lib/api-client";
import { isSignalDevAlert } from "@/lib/signal-dev";
import { useAlertPublicView } from "./AlertPaywallContext";
import { SignalPerformanceView } from "./signal-performance/SignalPerformanceView";
import {
  getMockSignalPerformance,
  type SignalPerformance,
} from "@/lib/mock/signal-performance";

export function AlertDashboardView({
  alertId,
  isOwner = false,
  viewMode,
}: {
  alertId: string;
  isOwner?: boolean;
  viewMode?: string;
}) {
  const publicView = useAlertPublicView();

  // Signals: try the track-record endpoint first. 404 (or any error) means
  // this is a normal alert → fall through to the HTML snapshot dashboard.
  const { data: signalPerf, isLoading: signalLoading } =
    useSWR<SignalPerformance | null>(
      alertId && !isSignalDevAlert(alertId) ? `signal-perf-${alertId}` : null,
      async () => {
        try {
          return (await apiClient.getSignalPerformance(
            alertId,
          )) as SignalPerformance;
        } catch {
          return null;
        }
      },
      {
        refreshInterval: 60_000,
        revalidateOnFocus: false,
        shouldRetryOnError: false,
      },
    );

  // Owner always sees freshest. Non-owner: LIVE only if entitled, else the
  // first-ever (delayed) snapshot — clicking the LIVE toggle paywalls.
  const mode: "latest" | "first" =
    isOwner || (publicView.view === "live" && publicView.canGoLive)
      ? "latest"
      : "first";

  const swrKey = alertId && !isSignalDevAlert(alertId) && !signalPerf && !signalLoading ? `alert-dash-${alertId}-${mode}` : null;
  const { data, isLoading, error } = useSWR<{ snapshot: AlertSnapshot } | null>(
    swrKey,
    async () => {
      if (!alertId) return null;
      try {
        return mode === "latest"
          ? await apiClient.getAlertSnapshotLatest(alertId)
          : await apiClient.getAlertSnapshotFirst(alertId);
      } catch {
        return null;
      }
    },
    {
      refreshInterval: mode === "latest" ? 30_000 : 0,
      revalidateOnFocus: false,
      dedupingInterval: 5_000,
      shouldRetryOnError: false,
    },
  );

  // Signal UI sandbox — never load this alert's real HTML dashboard.
  if (isSignalDevAlert(alertId)) {
    const mockSignal = getMockSignalPerformance("option");
    return (
      <div
        className="h-full min-h-0 bg-background"
        data-signal-dev-sandbox
        data-alert-id={alertId}
      >
        <SignalPerformanceView signal={mockSignal} isMock />
      </div>
    );
  }

  const snapshot = data?.snapshot ?? null;
  const html = snapshot?.html ?? null;
  const imageUrl = snapshot?.image_url ?? null;

  // Real signal — render the live track record instead of the HTML snapshot.
  if (signalPerf && (signalPerf as { product_type?: string }).product_type === "signal") {
    return (
      <div className="h-full min-h-0 bg-background" data-alert-id={alertId}>
        <SignalPerformanceView signal={signalPerf} />
      </div>
    );
  }

  if (isLoading || signalLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!snapshot || (!html && !imageUrl)) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="flex flex-col items-center gap-3 text-center max-w-sm">
          <div className={`flex h-12 w-12 items-center justify-center rounded-full ${viewMode === "signal" ? "bg-emerald-500/10 text-emerald-400" : "bg-sky-500/10 text-sky-400"}`}>
            <BarChart3 className="w-6 h-6" strokeWidth={1.8} />
          </div>
          <p className="text-sm font-semibold text-foreground">No dashboard yet</p>
          <p className="text-[12px] text-muted-foreground leading-relaxed">
            {error
              ? "Couldn't load this dashboard. It may still be running its first time."
              : "This alert hasn't captured a dashboard snapshot yet. It'll appear here after its next run."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Snapshot body — download lives in the header (DashboardDownloadMenu) */}
      <div className="flex-1 min-h-0 overflow-auto bg-white">
        {imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={imageUrl} alt="Alert dashboard" className="w-full h-auto block" />
        ) : (
          <iframe
            title="Alert dashboard"
            srcDoc={html ?? ""}
            sandbox="allow-scripts allow-same-origin allow-popups"
            className="w-full h-full border-0"
          />
        )}
      </div>
    </div>
  );
}
